import React, { useState, useEffect } from "react";
import {
  MapContainer as LeafletMap,
  TileLayer,
  CircleMarker,
  Popup,
} from "react-leaflet";
import { Card } from "react-bootstrap";
import axios from "axios";
import { fetchAnalyticsData } from "../dataService";
import { FaUser } from "react-icons/fa";

const GOOGLE_API_KEY = "AIzaSyD16h98hJe26HkkEvzKPbH2WytgT5srggM";

// ============================
//  FUNZIONE GEOCODING GOOGLE
// ============================
const getCoordinates = async (cityName, countryName) => {
  try {
    const query = `${cityName}, ${countryName}`;
    const response = await axios.get(
      "https://maps.googleapis.com/maps/api/geocode/json",
      {
        params: {
          address: query,
          key: GOOGLE_API_KEY,
        },
      },
    );

    if (response.data.status !== "OK") {
      console.warn("❗ Geocode fallito:", query, response.data.status);
      return null;
    }

    const location = response.data.results[0].geometry.location;
    return { lat: location.lat, lng: location.lng };
  } catch (error) {
    console.error("❌ Errore geocoding:", error);
    return null;
  }
};

// ================================
//   CACHE GEOCODING LOCALE
// ================================
const loadCache = () =>
  JSON.parse(localStorage.getItem("geocodeCache") || "{}");
const saveCache = (cache) =>
  localStorage.setItem("geocodeCache", JSON.stringify(cache));

const getCoordinatesCached = async (city, country) => {
  const cache = loadCache();
  const key = `${city}-${country}`;

  if (cache[key]) return cache[key];

  const coords = await getCoordinates(city, country);
  if (coords) {
    cache[key] = coords;
    saveCache(cache);
  }

  return coords;
};

// ==================================
//       COMPONENTE PRINCIPALE
// ==================================
const MapComponent = () => {
  const [citiesData, setCitiesData] = useState([]);
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);


  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  // ================================
  //        FETCH + GEOCODING
  // ================================
  useEffect(() => {
    const loadTPData = async () => {
      const analytics = await fetchAnalyticsData();
      const tpData = analytics?.TP?.mappa;
      if (!tpData) return;

      const combinedData = [
        ...tpData.ecommerce.map((c) => ({ ...c, tipo: "Ecommerce" })),
        ...tpData.istituzionale.map((c) => ({ ...c, tipo: "Istituzionale" })),
      ];

      const coordinatesArray = [];
      let totalUsers = 0;

      for (const city of combinedData) {
        if (!city.descrizioneCity) continue;
        const coords = await getCoordinatesCached(
          city.descrizioneCity,
          city.descrizioneCountry,
        );
        if (!coords) continue;

        const active = Number(city.activeUser);
        totalUsers += active;

        coordinatesArray.push({
          name: city.descrizioneCity,
          position: coords,
          visits: active,
          tipo: city.tipo,
        });
      }

      setCitiesData(coordinatesArray);
      setTotalActiveUsers(totalUsers);
    };

    loadTPData();
  }, []);

  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        const response = await fetch(
          "ws/index.php?Command=google.getAnalitycs",
        );
        const data = await response.json();

        // ✅ Estrae la sezione AR
        const tpData = data?.TP?.mappa;
        if (tpData) {
          const ecommerce = tpData.ecommerce || [];
          const istituzionale = tpData.istituzionale || [];

          console.log("Dati ricevuti per TP:", { ecommerce, istituzionale });

          // ✅ Somma tutti gli activeUser (sia ecommerce che istituzionale)
          const totalUsers = [...ecommerce, ...istituzionale].reduce(
            (sum, item) => {
              const val = parseInt(item.activeUser, 10);
              console.log(
                `Città: ${
                  item.descrizioneCity || "(sconosciuta)"
                } - activeUser: ${val}`,
              );
              return sum + (isNaN(val) ? 0 : val);
            },
            0,
          );

          console.log("Totale utenti online TP:", totalUsers);
          setOnlineUsers(totalUsers);
        } else {
          console.warn(
            "⚠️ Nessun dato 'TP.mappa' trovato nell'oggetto ricevuto.",
          );
          setOnlineUsers(0);
        }
      } catch (error) {
        console.error("❌ Errore nel recupero utenti online:", error);
      }
    };

    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 60000); // aggiorna ogni minuto
    return () => clearInterval(interval);
  }, []);

  // ======================================================
  //                     RENDER
  // ======================================================
  return (
    <>
      <Card
        className="hover-effect bg-light"
        style={{ minHeight: "230px", borderRadius: "10px" }}
      >
        {/* Conteggio utenti attivi */}
        <div
          className="text-title2w mx-auto mt-1"
          style={{
            padding: "3px",
            borderRadius: "5px",
            width: "100%",
            textAlign: "center",
            cursor: "pointer",
          }}
          onClick={handleShowModal}
        >
          
          <strong className="text-title d-flex justify-content-center align-items-center"> <FaUser className="me-2 text-dark fs-6" /> UTENTI ATTIVI ONLINE: {onlineUsers}</strong>
        </div>

        {/* MAPPA */}
        <LeafletMap
          style={{
            height: "100%",
            width: "100%",
            borderRadius: "0 0 10px 10px",
          }}
          center={[41.8719, 12.5674]} // Italia
          zoom={1}
          scrollWheelZoom={true}
          zoomControl={true}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          />

          {citiesData.map((city, index) => (
            <CircleMarker
              key={index}
              center={city.position}
              pathOptions={{
                color: "transparent",
                fillColor: city.tipo === "Ecommerce" ? "#b81f25" : "#585858",
                fillOpacity: 0.7,
                className: "blinking-marker",
              }}
              radius={Math.max(8, Math.sqrt(city.visits) * 1.5)}
            >
              <Popup>
                <div>
                  <h5>{city.name}</h5>
                  <p>Visite: {city.visits}</p>
                  <p>Tipo: {city.tipo}</p>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </LeafletMap>
      </Card>
    </>
  );
};

export default MapComponent;
